import { type RefObject, useEffect, useState } from "react";

export const useParentNavigationControl = (markerRef: RefObject<HTMLElement | null>, forceActive = false) => {
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);

	useEffect(() => {
		const parent = markerRef.current?.parentElement;
		if (!parent) return;

		// A row remounted under a resting pointer gets no pointerenter until the pointer leaves it.
		setIsHovered(parent.matches(":hover"));
		setIsFocused(parent.contains(document.activeElement));

		const hover = () => setIsHovered(true);
		const unhover = () => setIsHovered(false);
		const focus = () => setIsFocused(true);
		const blur = (event: FocusEvent) => {
			if (event.relatedTarget instanceof Node && parent.contains(event.relatedTarget)) return;
			setIsFocused(false);
		};

		parent.addEventListener("pointerenter", hover);
		parent.addEventListener("pointerleave", unhover);
		parent.addEventListener("focusin", focus);
		parent.addEventListener("focusout", blur);

		return () => {
			parent.removeEventListener("pointerenter", hover);
			parent.removeEventListener("pointerleave", unhover);
			parent.removeEventListener("focusin", focus);
			parent.removeEventListener("focusout", blur);
		};
	}, [markerRef]);

	return forceActive || isHovered || isFocused;
};
